import { Component, OnInit, ViewChild, inject } from '@angular/core';
import { AsyncState } from '@peca/backoffice/models/async-state.model';
import { Pageable } from '@peca/backoffice/models/pageable.model';
import { PageableQuery } from '@peca/backoffice/models/pageable-query.model';
import { ModalComponent, Pagination, PaginationInitialState, BreadcrumbItem } from '@peca/design-system';
import { PersistQueryService, ToastService } from '@peca/commons';
import { BehaviorSubject } from 'rxjs';
import { ConfirmableActionService } from '@peca/backoffice/services/confirmable-action.service';

import {
  AnyMarketExtractionDetailsDto,
  AnyMarketExtractionDto,
  AnyMarketExtractionFileDto,
  AnyMarketExtractionFilter,
} from '../../../models/anymarket-publication.model';
import { AnyMarketPublicationService } from '../../../services/anymarket-publication.service';

@Component({
  templateUrl: './anymarket-publisher.component.html',
  styleUrls: ['./anymarket-publisher.component.scss'],
  standalone: false,
})
export class AnyMarketPublisherComponent implements OnInit {
  @ViewChild('downloadModal') downloadModal!: ModalComponent;
  @ViewChild('filtersModal') filtersModal!: ModalComponent;

  private readonly service = inject(AnyMarketPublicationService);
  private readonly toast = inject(ToastService);
  private readonly persistQueryService = inject(PersistQueryService);
  private readonly confirmable = inject(ConfirmableActionService);

  readonly breadcrumbItems: BreadcrumbItem[] = [
    { label: 'Operações', path: '/operacoes' },
    { label: 'Anymarket', path: '/operacoes/anymarket' },
    { label: 'Publicações' },
  ];
  lastQuery: PageableQuery = {
    sort: 'audit.createdAt,desc',
    ...PaginationInitialState,
    page: 1,
  };
  readonly pagination$ = new BehaviorSubject<Pagination>({ ...PaginationInitialState, page: 1 });
  readonly statusLabels: Record<string, string> = {
    PENDING: 'Pendente',
    Pending: 'Pendente',
    PROCESSING: 'Processando',
    Processing: 'Processando',
    FINISHED: 'Concluída',
    Finished: 'Concluída',
    COMPLETED: 'Concluída',
    Completed: 'Concluída',
    FAILED: 'Falhou',
    Failed: 'Falhou',
    CANCELED: 'Cancelada',
    Canceled: 'Cancelada',
  };

  publicationsState: AsyncState<Pageable<AnyMarketExtractionDto>> = { loading: true };
  spreadsheets: AnyMarketExtractionFileDto[] = [];
  spreadsheetsLoading = false;
  spreadsheetsFailure = false;
  isDownloadingBatch = false;
  downloadingFileId: string | null = null;

  filters: FilterRow[] = [];
  filtersLoading = false;
  filtersFailure = false;
  selectedPublication?: AnyMarketExtractionDto;
  cancelingExtractionId: string | null = null;

  get publications() {
    return this.publicationsState.data?.items ?? [];
  }

  get query(): PageableQuery {
    const persistedQuery = this.persistQueryService.read<PageableQuery>('peca-anymarket-publication-list');
    const query = persistedQuery || this.lastQuery;
    if (query?.page === 0) query.page = 1;
    return query;
  }

  ngOnInit() {
    this.fetch(this.query);
  }

  fetch(pageableQuery: PageableQuery) {
    this.publicationsState = { loading: true };
    this.persistQueryService.rewriteQueryString('peca-anymarket-publication-list', pageableQuery);
    this.lastQuery = pageableQuery;
    this.service.list(pageableQuery).subscribe({
      next: (data) => {
        this.paginate(data);
        this.publicationsState = { data };
      },
      error: () => (this.publicationsState = { failure: true }),
    });
  }

  paginate(page: Pageable<unknown>) {
    const pagination = {
      page: page.currentPage,
      size: page.pageSize,
      total: page.totalPages,
      items: page.totalItems,
    };
    this.pagination$.next({ ...pagination, page: pagination.page });

    const persistedQuery = this.persistQueryService.read<PageableQuery>('peca-anymarket-publication-list');
    this.persistQueryService.rewriteQueryString('peca-anymarket-publication-list', { ...persistedQuery, ...pagination });
  }

  onPaginate(pagination: Pagination) {
    const page = pagination.page <= 0 ? 1 : pagination.page;
    const query = { ...this.lastQuery, ...pagination, page, sort: 'audit.createdAt,desc' };
    this.fetch(query);
  }

  refreshList() {
    this.fetch(this.lastQuery);
  }

  openDownloads(publication: AnyMarketExtractionDto) {
    this.selectedPublication = publication;
    this.spreadsheets = [];
    this.spreadsheetsLoading = true;
    this.spreadsheetsFailure = false;
    this.downloadModal.open();

    this.service.getById(publication.id).subscribe({
      next: (details) => {
        this.spreadsheets = details.files || [];
        this.spreadsheetsLoading = false;
        if (this.selectedPublication) {
          this.selectedPublication = {
            ...this.selectedPublication,
            status: details.status,
            audit: details.audit || this.selectedPublication.audit,
          };
        }
        this.syncPublicationInList(details);
      },
      error: () => {
        this.spreadsheetsLoading = false;
        this.spreadsheetsFailure = true;
      },
    });
  }

  refreshDetails() {
    if (!this.selectedPublication || this.spreadsheetsLoading) return;

    this.spreadsheetsLoading = true;
    this.spreadsheetsFailure = false;

    this.service.getById(this.selectedPublication.id).subscribe({
      next: (details) => {
        this.spreadsheets = details.files || [];
        this.spreadsheetsLoading = false;
        if (this.selectedPublication) {
          this.selectedPublication = {
            ...this.selectedPublication,
            status: details.status,
            audit: details.audit || this.selectedPublication.audit,
          };
        }
        this.syncPublicationInList(details);
        this.toast.success('Detalhes atualizados com sucesso.');
      },
      error: () => {
        this.spreadsheetsLoading = false;
        this.spreadsheetsFailure = true;
        this.toast.failure('Erro ao atualizar detalhes.');
      },
    });
  }


  private syncPublicationInList(details: AnyMarketExtractionDetailsDto) {
    const items = this.publicationsState.data?.items;
    if (!items) return;

    const index = items.findIndex((p) => p.id === details.id);
    if (index !== -1) {
      items[index] = {
        ...items[index],
        status: details.status,
        audit: details.audit || items[index].audit,
      };
    }
  }

  downloadSpreadsheet(spreadsheet: AnyMarketExtractionFileDto) {
    if (!this.selectedPublication) return;

    const fileIdentifier = spreadsheet.name || spreadsheet.id;
    if (this.downloadingFileId === fileIdentifier || this.isDownloadingBatch) return;

    this.downloadingFileId = fileIdentifier;

    this.service.getDownloadUrls(this.selectedPublication.id, [fileIdentifier]).subscribe({
      next: (urls) => {
        this.downloadingFileId = null;
        if (!urls || urls.length === 0) {
          this.toast.failure('Nenhum link de download disponível.');
          return;
        }

        this.triggerDownload(urls[0], spreadsheet.name);
        this.toast.success('Download iniciado com sucesso.');
      },
      error: () => {
        this.downloadingFileId = null;
        this.toast.failure('Erro ao gerar link de download.');
      },
    });
  }

  downloadAllSpreadsheets() {
    if (!this.selectedPublication || this.spreadsheets.length === 0 || this.isDownloadingBatch) return;

    this.isDownloadingBatch = true;
    const allFiles = this.spreadsheets.map((s) => s.name || s.id);

    this.service.getDownloadUrls(this.selectedPublication.id, allFiles).subscribe({
      next: (urls) => {
        if (!urls || urls.length === 0) {
          this.isDownloadingBatch = false;
          this.toast.failure('Nenhum link de download disponível.');
          return;
        }

        urls.forEach((url, index) => {
          setTimeout(() => {
            this.triggerDownload(url, this.spreadsheets[index]?.name);
            if (index === urls.length - 1) {
              this.isDownloadingBatch = false;
              this.toast.success('Downloads iniciados com sucesso.');
            }
          }, index * 500);
        });
      },
      error: () => {
        this.isDownloadingBatch = false;
        this.toast.failure('Erro ao gerar links de download.');
      },
    });
  }

  private triggerDownload(url: string, filename?: string) {
    fetch(url)
      .then((response) => {
        if (!response.ok) throw new Error('Fetch failed');
        return response.blob();
      })
      .then((blob) => {
        const blobUrl = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = blobUrl;
        if (filename) {
          a.download = filename;
        }
        a.click();
        a.remove();
        setTimeout(() => window.URL.revokeObjectURL(blobUrl), 10000);
      })
      .catch(() => {
        // Fallback: hidden iframe to avoid top-level window navigation and tab blinking
        const iframe = document.createElement('iframe');
        iframe.style.display = 'none';
        iframe.src = url;
        document.body.appendChild(iframe);
        setTimeout(() => iframe.remove(), 60000);
      });
  }

  openFilters(publication: AnyMarketExtractionDto) {
    this.selectedPublication = publication;
    this.filters = [];
    this.filtersLoading = true;
    this.filtersFailure = false;
    this.filtersModal.open();

    this.service.getById(publication.id).subscribe({
      next: (details) => {
        this.filters = this.flattenFilters(details.filters);
        this.filtersLoading = false;
      },
      error: () => {
        this.filtersLoading = false;
        this.filtersFailure = true;
      },
    });
  }

  isProcessing(publication: AnyMarketExtractionDto): boolean {
    return publication.status?.toUpperCase() === 'PROCESSING';
  }

  cancelExtraction(publication: AnyMarketExtractionDto) {
    if (this.cancelingExtractionId) return;

    this.confirmable.confirm('Você está cancelando esta publicação.', publication.id, true).subscribe((confirmation) => {
      if (!confirmation.confirmed || this.cancelingExtractionId) return;

      this.cancelingExtractionId = publication.id;
      this.service.cancel(publication.id).subscribe({
        next: () => {
          this.cancelingExtractionId = null;
          this.toast.success('Extração cancelada com sucesso.');
          this.refreshList();
        },
        error: () => {
          this.cancelingExtractionId = null;
          this.toast.failure('Não foi possível cancelar a extração.');
        },
      });
    });
  }

  closeDownloads() {
    this.downloadModal.close();
    this.selectedPublication = undefined;
  }

  closeFilters() {
    this.filtersModal.close();
    this.selectedPublication = undefined;
  }

  formatSpreadsheetSize(size: number) {
    if (size < 1024) return `${size} B`;
    if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
    return `${(size / (1024 * 1024)).toFixed(1)} MB`;
  }

  private flattenFilters(filters?: AnyMarketExtractionFilter | null): FilterRow[] {
    if (!filters) return [];

    const rows: FilterRow[] = [];

    if (filters.brandsId) {
      const mode = filters.brandsId.isBlacklist ? 'Blacklist (Exclusão)' : 'Whitelist (Inclusão)';
      rows.push({
        key: `Marcas (${mode})`,
        value: filters.brandsId.values?.length ? filters.brandsId.values : ['Todas as marcas (nenhuma exclusão/filtro)'],
      });
    }

    if (filters.productGroupsId) {
      const mode = filters.productGroupsId.isBlacklist ? 'Blacklist (Exclusão)' : 'Whitelist (Inclusão)';
      rows.push({
        key: `Grupos de Produto (${mode})`,
        value: filters.productGroupsId.values?.length ? filters.productGroupsId.values : ['Todos os grupos (nenhuma exclusão/filtro)'],
      });
    }

    if (filters.branches) {
      const mode = filters.branches.isBlacklist ? 'Blacklist (Exclusão)' : 'Whitelist (Inclusão)';
      const values = filters.branches.values?.length
        ? filters.branches.values.map((b) => (b.seller ? `${b.seller} - ${b.branch}` : b.branch))
        : ['Todos os vendedores/filiais'];
      rows.push({
        key: `Vendedores / Filiais (${mode})`,
        value: values,
      });
    }

    if (filters.deliveryTimeInMinutes !== undefined && filters.deliveryTimeInMinutes !== null) {
      rows.push({
        key: 'Prazo de entrega',
        value: [`${filters.deliveryTimeInMinutes} minutos`],
      });
    }

    return rows;
  }
}

interface FilterRow {
  key: string;
  value: string[];
}
